import React, {useCallback, useEffect, useState} from 'react'
import {Box, Stack, Typography} from '@mui/material'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import {PREVIEW_MIME_TYPES, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface DossierTabProps {
  patientId: string
  firstName?: string
  lastName?: string
}

interface DossierRow {
  _id: string
  createdAt: string
  uploadedFile: string
  [key: string]: any
}

interface DossierColumnConfig {
  id: string
  label: string
}

const DEFAULT_COLUMNS: DossierColumnConfig[] = [
  {id: 'createdAt', label: 'GENERATO IL'},
]

const INITIAL_VIEWER_STATE = {open: false, filePath: '', mime: '', fileName: '', status: STATUS.PENDING}

function buildDossierFileName(row: DossierRow, firstName?: string, lastName?: string): string {
  return `Dossier - ${firstName ?? ''} ${lastName ?? ''} - ${moment(row.createdAt).format('DD/MM/YYYY')}`
}

function renderCell(id: string, firstName?: string, lastName?: string) {
  return (row: DossierRow): React.ReactNode => {
    if (id === 'createdAt') {
      return (
        <Stack alignItems="center" direction="row" spacing={1.5}>
          <InsertDriveFileOutlinedIcon sx={{color: NEUTRAL_SCALE[500], fontSize: 22}} />
          <Box>
            <Typography fontWeight={600} variant="body2">{buildDossierFileName(row, firstName, lastName)}</Typography>
            <Typography color="text.secondary" variant="caption">
              {row.createdAt ? moment(row.createdAt).format('DD MMMM YYYY, HH:mm') : '—'}
            </Typography>
          </Box>
        </Stack>
      )
    }
    return <Typography variant="body2">{row[id] ?? '—'}</Typography>
  }
}

/**
 * Read-only list of generated dossier exports for a patient. Row click and the
 * download row-action both hit the same download endpoint (which returns the
 * file bytes plus its content-type header); the mime type is only known once
 * that response arrives, so — unlike ActivityDocumentsTabNew, which can derive
 * the mime from the filename up front — we can't open the preview dialog in a
 * pending state before fetching. We fetch first, then either preview or
 * force-download depending on the resolved content-type.
 */
export default function DossierTab({patientId, firstName, lastName}: DossierTabProps): JSX.Element {
  const dossierColumnOptions: DossierColumnConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.dossierConfiguration?.columnOptions
  )

  const [rows, setRows] = useState<DossierRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [statusDownload, setStatusDownload] = useState<string | undefined>()
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-createdAt'})
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getPatientsDossiersExports(patientId, setStatus, pagination),
      apiClient.getPatientsDossiersExportsCount(patientId, setStatusCount),
    ])
      .then(([dossiers, countResult]) => {
        if (Array.isArray(dossiers)) setRows(dossiers)
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) setViewerState(INITIAL_VIEWER_STATE)
  }, [statusDownload])

  const forceDownload = (fileResponse: any, downloadName: string) => {
    const blob = new Blob([new Uint8Array(fileResponse.data)], {type: fileResponse.headers['content-type']})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = downloadName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  const handleDownload = async (row: DossierRow) => {
    const downloadName = buildDossierFileName(row, firstName, lastName)
    const fileResponse = await apiClient.downloadPatientsDossierExport(patientId, row.uploadedFile, setStatusDownload)
    if (!fileResponse) return
    forceDownload(fileResponse, downloadName)
  }

  const handleRowClick = async (row: DossierRow) => {
    const downloadName = buildDossierFileName(row, firstName, lastName)
    const fileResponse = await apiClient.downloadPatientsDossierExport(patientId, row.uploadedFile, setStatusDownload)
    if (!fileResponse) return

    const mime = fileResponse.headers['content-type']
    if (PREVIEW_MIME_TYPES.includes(mime)) {
      const blob = new Blob([new Uint8Array(fileResponse.data)], {type: mime})
      const url = (window.URL || window.webkitURL).createObjectURL(blob)
      setViewerState({open: true, filePath: url, mime, fileName: downloadName, status: STATUS.FULFILLED})
      return
    }

    forceDownload(fileResponse, downloadName)
  }

  const columnConfig = dossierColumnOptions?.length > 0 ? dossierColumnOptions : DEFAULT_COLUMNS

  const columns: ColumnDef<DossierRow>[] = [
    ...columnConfig.map(col => ({
      key: col.id,
      header: col.label,
      render: renderCell(col.id, firstName, lastName),
      ...(col.id === 'createdAt' ? {sortKey: 'createdAt', mobileCardHeader: true} : {}),
    })),
    {
      key: 'actions',
      header: '',
      width: 56,
      isActionColumn: true,
      render: (row: DossierRow) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <ActionIconButton variant="download" tooltip="Scarica" onClick={() => handleDownload(row)} />
        </Box>
      ),
    },
  ]

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel recuperare i files.'}
        </InlineMessage>
      )}

      {statusDownload === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDownload(undefined)} sx={{mb: 2}}>
          {'Errore nel scaricare il documento.'}
        </InlineMessage>
      )}

      <DataTable<DossierRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun dossier trovato"
        onRowClick={handleRowClick}
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortBy={pagination.order}
        onSortChange={(order) => setPagination(prev => ({...prev, order, page: 0}))}
      />

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={() => setViewerState(INITIAL_VIEWER_STATE)}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
